import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import type {
  OnboardingAction,
  OnboardingChecklistItem,
  OnboardingNudge,
} from 'fym-shared/src/music24/onboardingChecklist';
import { TypedMemo } from '../../common/types';
import { checklistItemMessages, checklistMessages as messages } from './checklistMessages';
import { ChecklistNudgeButton } from './ChecklistNudgeButton';
import { withBrandWord } from '../../music24/components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the checklist; each part uses its own slice of it */
import styles from './FirstReleaseChecklist.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChecklistItemRowProps {
  item: OnboardingChecklistItem;
  /** The first item that is not done: Home highlights it. */
  isNext: boolean;
  /** `full` is the Settings -> Setup row with the body and the button (design d6). */
  variant: 'compact' | 'full';
  /** The ready asset count for the `planPick` body. */
  readyAssets: number;
  onAction: (action: OnboardingAction) => void;
  /** Runs the nudge of a done item (the cover of the first-release song). No nudge button without it. */
  onNudge?: (nudge: OnboardingNudge) => void;
  /**
   * Set when the caller's role cannot run the action of this item (a member and the plan pick). The
   * row then shows this note in place of the button.
   */
  lockedNote?: string;
}

/**
 * One row of the checklist (design: onboarding d1 right column, d6). A done item with a nudge
 * (owner answer 4, 2026-10-09: the song has no cover) keeps its check and shows "Add a cover".
 */
const ChecklistItemRowComponent: React.FC<ChecklistItemRowProps> = ({
  item,
  isNext,
  variant,
  readyAssets,
  onAction,
  onNudge,
  lockedNote,
}) => {
  const intl = useIntl();
  const nudge = item.done && onNudge !== undefined ? item.nudge : undefined;
  const nudgeButton =
    nudge !== undefined && onNudge !== undefined ? <ChecklistNudgeButton nudge={nudge} onNudge={onNudge} /> : null;
  const copy = checklistItemMessages[item.key];
  const runAction = useCallback(() => onAction(item.action), [item.action, onAction]);
  const check = <span className={styles.check}>{item.done ? <FontAwesomeIcon icon={faCheck} /> : null}</span>;

  if (variant === 'compact') {
    return (
      <li
        className={clsx(
          styles.item,
          item.done && styles.itemDone,
          !item.done && isNext && styles.itemNow,
          nudgeButton !== null && styles.itemWithNudge
        )}
        data-testid={`checklist-item-${item.key}`}
        data-done={item.done ? 'true' : 'false'}
      >
        {check}
        <span className={styles.itemTitle}>{intl.formatMessage(copy.title)}</span>
        {nudgeButton}
      </li>
    );
  }

  return (
    <li
      className={clsx(styles.fullItem, item.done && styles.fullItemDone)}
      data-testid={`checklist-item-${item.key}`}
      data-done={item.done ? 'true' : 'false'}
    >
      {check}
      <div>
        <b>{intl.formatMessage(copy.title)}</b>
        <small>
          {nudge !== undefined
            ? intl.formatMessage(messages.nudgeCoverBody)
            : withBrandWord(intl.formatMessage(copy.body, { count: readyAssets }))}
        </small>
      </div>
      {nudgeButton}
      {item.done && nudgeButton === null ? (
        <span className={styles.tiny}>{intl.formatMessage(messages.itemDone)}</span>
      ) : null}
      {!item.done && lockedNote !== undefined ? (
        <span className={styles.tiny} data-testid={`checklist-item-${item.key}-locked`}>
          {lockedNote}
        </span>
      ) : null}
      {!item.done && lockedNote === undefined ? (
        <button type="button" className={styles.actionLink} onClick={runAction}>
          {intl.formatMessage(copy.action)}
        </button>
      ) : null}
    </li>
  );
};

export const ChecklistItemRow = TypedMemo(ChecklistItemRowComponent);
